/**
 * offlineManager.js — Core offline logic singleton
 *
 * Responsibilities:
 *  - Track mode ('online' | 'offline') per branch (persisted in localStorage)
 *  - Track real network connectivity
 *  - Route order / KDS operations to API or localDB depending on current state
 *  - Drive Online-Mode auto-sync when the network restores
 *  - Drive Offline-Mode manual sync
 *  - Keep Redux offline slice in sync
 */

import localDB from './localDB';
import orderService from './orderService';
import api from '../utils/api';
// sessionService imported lazily (circular-dep safe) inside _syncSessions

// Status ranking for conflict resolution (local wins by timestamp, but status must
// not go backwards unless the server has a strictly newer record).
const STATUS_ORDER = ['draft', 'confirmed', 'preparing', 'ready', 'served', 'billed', 'completed', 'cancelled'];
export function statusRank(s) {
  const i = STATUS_ORDER.indexOf(s);
  return i === -1 ? 0 : i;
}

// An order id is "localish" when the server doesn't know about it yet — it
// was minted by generateLocalId() (local_xxx) or by the in-cart draft path
// (draft_xxx). PUTting against such an id would 404; we route to local
// persistence instead and let the next sync remap to the real server UUID.
function isLocalishOrderId(orderId) {
  if (!orderId) return false;
  const s = String(orderId);
  return s.startsWith('local_') || s.startsWith('draft_');
}

function generateLocalId(reason = 'unknown') {
  const id = `local_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // Trace every minted local_id so two ids generated milliseconds apart for
  // the "same" cart can be tied back to their actual call origins. The stack
  // trace is captured by triggering an Error and reading its stack — gives
  // us the chain of callers without breaking the call.
  let stack = '';
  try { stack = (new Error('localid-trace').stack || '').split('\n').slice(2, 7).join(' › '); } catch (_) {}
  console.warn('[create-order] generateLocalId', { id, reason, stack });
  return id;
}

/**
 * True when the API is unreachable or the response is clearly a gateway/connectivity
 * failure — fall back to local POS storage (Wi‑Fi can be "on" with no real internet,
 * captive portals, DNS failures, timeouts, etc.).
 */
function isNetworkError(error) {
  if (!error) return false;

  if (error.code === 'ERR_CANCELED') return false;

  const status = error.response?.status;
  if (status === 408 || status === 502 || status === 503 || status === 504) {
    return true;
  }

  if (error.isAxiosError && error.request && !error.response) {
    return true;
  }

  const code = error.code;
  const msg = String(error.message || '').toLowerCase();

  return (
    !error.response ||
    code === 'ERR_NETWORK' ||
    code === 'ECONNREFUSED' ||
    code === 'ECONNABORTED' ||
    code === 'ETIMEDOUT' ||
    code === 'ENOTFOUND' ||
    code === 'EAI_AGAIN' ||
    code === 'ECONNRESET' ||
    code === 'ERR_INTERNET_DISCONNECTED' ||
    error.message === 'Network Error' ||
    msg.includes('network error') ||
    msg.includes('timeout') ||
    msg.includes('timed out') ||
    msg.includes('failed to fetch')
  );
}

// ─── Constants ───────────────────────────────────────────────────────────────

/** Orders sent to the server per HTTP request during sync. */
const SYNC_BATCH_SIZE = 50;

/**
 * After this many days of unsynced local data, the SyncProgressModal will
 * display a data-age warning so staff know to verify records manually.
 */
const RETENTION_WARNING_DAYS = 7;

// ─── OfflineManager class ─────────────────────────────────────────────────────

class OfflineManager {
  constructor() {
    this._mode            = 'online'; // branch-level setting
    this._isNetworkOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
    this._branchId        = null;
    this._store           = null;
    this._listenersSetup  = false;
    /** Deep Fix A: prevents concurrent sync calls from the same device */
    this._syncInProgress  = false;
    /** Recovery probe: detects internet returning when the OS 'online' event never fires */
    this._recoveryTimer   = null;
    /** Keep-alive: pings the API so a free-tier server doesn't spin down and cold-start orders into offline. */
    this._keepAliveTimer  = null;
    /** Phase B local-first: debounced background sync pump */
    this._pumpTimer       = null;
    this._pumpBackoffUntil = 0;
  }

  // ─── Initialization ─────────────────────────────────────────────────────────

  /**
   * Call once when a branch is loaded into the POS.
   * Reads the saved mode for this branch and wires network listeners.
   */
  init(store, branchId) {
    this._store    = store;
    this._branchId = branchId;

    // Load persisted mode for this branch
    const saved = localStorage.getItem(`pos_mode_${branchId}`);
    this._mode = saved === 'offline' ? 'offline' : 'online';

    // Wire network listeners once
    if (!this._listenersSetup) {
      this._listenersSetup = true;
      window.addEventListener('online',  () => this._handleNetworkRestore());
      window.addEventListener('offline', () => this._handleNetworkLoss());
    }

    // Push initial state to Redux
    this._dispatchSlice('offline/setMode',          this._mode);
    this._dispatchSlice('offline/setNetworkOnline', this._isNetworkOnline);

    // Restore online the instant any API request succeeds (no-op if already online).
    api.onResponseSuccess = () => this._handleNetworkRestore();

    // Keep the server warm so quiet periods don't cold-start orders into offline.
    this._startKeepAlive();

    // Compute and push pending counts
    this._updatePendingCounts();

    // Boot drain: rows left over from the previous run (app closed mid-pump,
    // backoff at shutdown) upload without waiting for the next write.
    this._requestPump('boot');
  }

  // ─── Mode management ────────────────────────────────────────────────────────

  setMode(mode, branchId) {
    const bid = branchId || this._branchId;
    this._mode = mode;
    if (bid) localStorage.setItem(`pos_mode_${bid}`, mode);
    this._dispatchSlice('offline/setMode', mode);
    if (typeof window !== 'undefined' && window.electronAPI?.isElectron) {
      import('./socketService').then(({ default: socketService }) => {
        socketService.registerPrintStation(bid);
      }).catch(() => {});
    }
  }

  getMode()          { return this._mode; }
  isNetworkOnline()  { return this._isNetworkOnline; }

  /** True when we must save locally instead of hitting the API */
  shouldUseLocal() {
    return this._mode === 'offline' || !this._isNetworkOnline;
  }

  /**
   * Phase B local-first: on the Electron fleet, EVERY order write persists to
   * local storage first and returns immediately — the server learns about it
   * from the background sync pump. The screen never waits on the network.
   *
   * Browser devices (waiter tablets) deliberately stay server-first: their
   * kitchen tickets print on the counter's print station via live socket
   * emits, and sync-replayed events carry syncReplay:true which print
   * stations SKIP — a local-first create from a tablet would never print.
   * The ensureRemotePrintStationReady gate depends on this split too.
   */
  localFirstWrites() {
    return typeof window !== 'undefined' && !!window.electronAPI?.isElectron;
  }

  /**
   * Storage key for an order's queue row. Once an order has a real server id,
   * its row must be KEYED by that id and carry localId = that same id — if an
   * edit row kept the original local_ key after sync, the replay's create-path
   * dedup would match the already-synced local_id and DISCARD the edit
   * (create-dedup maps the id and skips the payload).
   */
  _rowKeyFor(order, fallbackId) {
    const serverId = [order?._id, order?.id, fallbackId].find(
      (v) => v && !isLocalishOrderId(v)
    );
    if (serverId) return String(serverId);
    return String(order?.localId || order?._id || order?.id || fallbackId || '');
  }

  // ─── Background sync pump (Phase B) ─────────────────────────────────────────

  /**
   * Ask for a background sync soon. Debounced so a burst of writes (add item,
   * add item, place order) produces ONE sync. Connectivity is checked at fire
   * time, not request time — a pump requested offline simply does nothing
   * until the next write after restore (the reconnect modal path covers the
   * backlog case).
   */
  _requestPump(reason) {
    if (!this.localFirstWrites()) return;
    if (this._pumpTimer) return; // already scheduled — the debounce window absorbs this write
    const PUMP_DEBOUNCE_MS = 2_500;
    this._pumpTimer = setTimeout(() => {
      this._pumpTimer = null;
      this._runQuietSync(reason);
    }, PUMP_DEBOUNCE_MS);
  }

  /** Public pump request (e.g. after recording a deleted-local marker). */
  requestSync(reason = 'external') {
    this._requestPump(reason);
  }

  /**
   * Stamp a few fields onto an order's NEWEST durable copy — never a
   * whole-order snapshot write. Field stamps from UI components (billedAt
   * after a bill print) used to persist the component's render-scope order
   * object; fired milliseconds after a modification finished, that stale
   * snapshot overwrote the fresh queue row and the pump replayed the OLD
   * cart to the server — staff watched their just-added item vanish
   * (Biryani order c3b65e20, 2026-08-12 16:35, three attempts to keep one
   * item). Base preference: the queue row (newest durable copy) → the
   * caller's freshest store lookup.
   */
  async stampOrderFields(orderId, patch, fallbackOrder = null) {
    try {
      const key = this._rowKeyFor(fallbackOrder || {}, orderId) || String(orderId);
      const row = await localDB.getOrder(key).catch(() => null);
      const base = row || fallbackOrder;
      if (!base) return;
      await this._persistOrderLocally({ ...base, ...patch, localId: key });
      this._requestPump('stampOrderFields');
    } catch (err) {
      console.warn('[offline] stampOrderFields failed (non-fatal)', {
        orderId, fields: Object.keys(patch || {}), errMsg: String(err?.message || err),
      });
    }
  }

  /** True while ANY sync (pump or modal) holds the engine lock. */
  isSyncInProgress() {
    return this._syncInProgress === true;
  }

  async _runQuietSync(reason) {
    if (this.shouldUseLocal()) return;            // offline — replay path owns recovery
    if (Date.now() < this._pumpBackoffUntil) return; // recent failure — don't hammer
    if (this._syncInProgress) {                   // modal or a prior pump is mid-flight
      this._requestPump('rescheduled');
      return;
    }
    try {
      // Nothing pending → nothing to say. Skips the empty round-trip a
      // rescheduled pump would otherwise make right after a sync settled
      // everything. Deleted-local markers count as pending work too.
      const counts = await this.getPendingCounts(this._branchId);
      let markerCount = 0;
      try {
        const { getDeletedLocalIds } = await import('./deletedLocalMarkers');
        markerCount = getDeletedLocalIds().length;
      } catch (_) { /* markers unreadable — treat as none */ }
      if ((counts?.orders || 0) === 0 && (counts?.kdsEvents || 0) === 0 && markerCount === 0) return;
    } catch (_) { /* counts unreadable — sync anyway, it's harmless */ }
    try {
      // Tell the UI a sync is genuinely running — the pending banner then
      // shows "Syncing to server…" instead of a clickable "waiting to sync"
      // that invites a manual sync straight into the engine lock (Biryani
      // 1.0.56 first-order test: staff tap → fake SYNC FAILED ticker).
      this._dispatchSlice('offline/setSyncing', true);
      const { runOnlineSyncAndApply } = await import('./syncRunner');
      const res = await runOnlineSyncAndApply(this._branchId, {
        dispatch: (a) => this._store?.dispatch(a),
        getState: () => this._store?.getState(),
        quiet: true,
      });
      if ((res?.failed || 0) > 0 || (res?.errors?.length || 0) > 0) {
        // Quiet pump means no modal — failures MUST still reach the cloud
        // log feed loudly, or a rejected order dies silently.
        console.error('[sync] background pump had per-order failures', {
          branchId: this._branchId,
          failed: res.failed,
          errors: (res.errors || []).slice(0, 5),
        });
        // The sync call itself succeeded, so the catch-side backoff retry
        // never fires — yet these rows carry state the server rejected
        // (often transient: a 5xx on one order, a session race). Without a
        // scheduled retry they sit queued until the NEXT user write, which
        // on a closing shift can be tomorrow. One slow-cadence retry at a
        // time; each retry re-arms itself if rows still fail.
        if (!this._failedRowRetryTimer) {
          this._failedRowRetryTimer = setTimeout(() => {
            this._failedRowRetryTimer = null;
            this._requestPump('failed-rows-retry');
          }, 60_000);
        }
      }
    } catch (err) {
      // A concurrent sync grabbed the lock between our check and the call —
      // not a failure; that sync carries our rows. Just reschedule.
      if (String(err?.message || '').includes('Sync already in progress')) {
        this._requestPump('lock-collision');
        return;
      }
      // Network-class failures already flipped us offline inside syncOnlineMode
      // (probe restarts, reconnect path takes over). Anything else: back off
      // so a 500ing server isn't hammered on every keystroke; rows stay
      // safely queued, and a retry is SCHEDULED — without it, the last writes
      // of a quiet night could sit pending until the next user action
      // (adversarial review: pump strand findings).
      this._pumpBackoffUntil = Date.now() + 30_000;
      setTimeout(() => this._requestPump('backoff-retry'), 31_000);
      console.warn('[sync] background pump failed — backing off 30s', {
        branchId: this._branchId,
        reason,
        errStatus: err?.response?.status,
        errMsg: err?.message,
      });
    } finally {
      this._dispatchSlice('offline/setSyncing', false);
    }
  }

  /**
   * Drain the queue NOW (no debounce) and wait for it. Used before
   * "definitive" moments — End Session / End Day — so the books close over
   * what the server actually has. Never throws; a failed drain leaves rows
   * queued exactly as before.
   */
  async drainNow(reason = 'drain') {
    if (this._pumpTimer) {
      clearTimeout(this._pumpTimer);
      this._pumpTimer = null;
    }
    this._pumpBackoffUntil = 0; // a deliberate drain overrides the backoff
    // If a sync is already mid-flight, WAIT for it instead of silently
    // returning — the caller is about to close the books over "what synced".
    const WAIT_MS = 20_000;
    const start = Date.now();
    while (this._syncInProgress && Date.now() - start < WAIT_MS) {
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
    try {
      await this._runQuietSync(reason);
    } catch (_) { /* _runQuietSync handles its own failures */ }
  }

  // ─── Order operations ────────────────────────────────────────────────────────

  /**
   * Create an order.
   * Online + connected  → API, with network-error fallback to local.
   * Offline mode OR no network → local only.
   */
  async createOrder(orderPayload, branchId) {
    if (!Array.isArray(orderPayload.items) || orderPayload.items.length === 0) {
      throw new Error('Cannot create an order with an empty cart');
    }

    const bid = branchId || this._branchId;

    // Idempotency key generated BEFORE the first server attempt.
    // The server stores it as local_id on the online create, and the same key is
    // reused for the offline fallback save. If the create succeeds on the server
    // but the response is lost (timeout / Wi-Fi blip), the fallback order syncs
    // with this same key and the server dedups on local_id instead of inserting
    // a second row. Without this, a lost create-response produced a duplicate order.
    // A draft_ id must NEVER become the idempotency key: a hydrated draft
    // carries its localId into the create payload, the durable row then gets
    // keyed draft_..., and BOTH the draft cleanup and the sync's draft filter
    // treat it as a cart — the order is deleted locally / never uploads
    // (audit: critical, create-path). Drafts are carts; orders mint local_.
    const incomingLocalId = orderPayload.localId || orderPayload.local_id || null;
    const usableLocalId =
      incomingLocalId && !String(incomingLocalId).startsWith('draft_') ? incomingLocalId : null;
    if (incomingLocalId && !usableLocalId) {
      console.warn('[create-order] discarded draft_ localId as idempotency key', {
        draftId: incomingLocalId,
      });
    }
    const _payloadHadLocalId = !!usableLocalId;
    const localId = usableLocalId || generateLocalId('offlineManager.createOrder');
    const payload = { ...orderPayload, localId, local_id: localId };

    // Capture every entry into createOrder so a sync-driven re-create or a
    // double-click race is visible end-to-end. Reused localId means the
    // caller had already minted one (correct dedup); fresh means a NEW id
    // was just generated by this call (potential dup source).
    console.warn('[create-order] offlineManager.createOrder', {
      branchId: bid,
      localId,
      reusedLocalId: _payloadHadLocalId,
      itemCount: orderPayload?.items?.length || 0,
      orderType: orderPayload?.orderType || orderPayload?.order_type,
      total: orderPayload?.total,
      shouldUseLocal: this.shouldUseLocal(),
      networkOnline: this._isNetworkOnline,
      mode: this._mode,
    });

    if (this.shouldUseLocal()) {
      console.warn('[offline] createOrder → local (offline mode or network down)', {
        branchId: bid,
        localId,
        items: payload.items?.length || 0,
      });
      return this._saveNewOrderLocally(payload, bid);
    }

    // ── Phase B local-first (Electron): the order is durable on disk and on
    // screen in milliseconds; the pump replays it to the server in the
    // background (same idempotent local_id path the offline sync uses, so a
    // double delivery can never duplicate). The kitchen ticket prints from
    // the local row; the replay's order_created emit carries syncReplay:true
    // so print stations skip it.
    if (this.localFirstWrites()) {
      console.warn('[create-order] local-first create — server via background pump', {
        branchId: bid,
        localId,
      });
      const localOrder = await this._saveNewOrderLocally(payload, bid);
      this._requestPump('create');
      return localOrder;
    }

    try {
      const response = await orderService.createOrder(payload);
      return response.order || response;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('[offline] createOrder server unreachable — falling back to local', {
          branchId: bid,
          localId,
          errCode: error?.code,
          errMsg: error?.message,
        });
        this._handleNetworkLoss();
        return this._saveNewOrderLocally(payload, bid);
      }
      throw error;
    }
  }

  /**
   * Force-save an order locally even when its create failure wasn't classified
   * as a network error. Used when the kitchen ticket already printed (via the
   * early print gate) before the create settled — at that point losing the
   * order is worse than risking a duplicate, so it must land somewhere instead
   * of vanishing with just an error toast. Reuses the same idempotency local_id
   * shape as createOrder so it still dedups correctly if the original request
   * actually succeeded server-side.
   */
  async forceLocalOrder(orderPayload, branchId) {
    const bid = branchId || this._branchId;
    const _payloadHadLocalId = !!(orderPayload.localId || orderPayload.local_id);
    const localId = orderPayload.localId || orderPayload.local_id || generateLocalId('offlineManager.forceLocalOrder');
    const payload = { ...orderPayload, localId, local_id: localId, status: orderPayload.status || 'confirmed' };
    console.warn('[create-order] offlineManager.forceLocalOrder', {
      branchId: bid, localId, reusedLocalId: _payloadHadLocalId,
      itemCount: orderPayload?.items?.length || 0,
    });
    this._handleNetworkLoss();
    return this._saveNewOrderLocally(payload, bid);
  }

  /**
   * Update an order's full data.
   * `currentOrder` is the current Redux version (used when offline so we have
   * something to persist).
   */
  /**
   * Record a recent-close tombstone when an order reaches a terminal status.
   * Local truth says the table is free; for the next 3 minutes any server
   * snapshot that still claims this order is ACTIVE gets filtered out by
   * withoutStaleActives (Ayder table 4: post-payment snapshot re-occupied
   * the table). Records every alias id so snapshots match under any key.
   */
  _recordCloseTombstone(orderId, currentOrder, status) {
    if (status !== 'completed' && status !== 'cancelled') return;
    import('./deletedLocalMarkers')
      .then(({ recordRecentClose }) => {
        recordRecentClose(orderId);
        const sid = currentOrder?._id || currentOrder?.id;
        if (sid && String(sid) !== String(orderId)) recordRecentClose(sid);
        if (currentOrder?.localId) recordRecentClose(currentOrder.localId);
      })
      .catch(() => { /* tombstone is best-effort */ });
  }

  async updateOrder(orderId, orderData, currentOrder) {
    if (this.shouldUseLocal()) {
      const saved = await this._persistOrderLocally({ ...(currentOrder || {}), ...orderData, localId: currentOrder?.localId || orderId });
      if (saved) this._recordCloseTombstone(orderId, currentOrder, orderData?.status);
      return saved;
    }

    // ── Phase B local-first (Electron): ALL updates — payments, discounts,
    // item edits, status fields — persist locally first (durable, with the
    // paid-field preservation guard) and reach the server via the pump's
    // sync replay, which carries the statusRank / terminal-immunity /
    // money-recompute guards. The UI resolves from the local row in ms.
    // This uniformly covers the not-yet-synced case too (local_ ids replay
    // as creates; server-id rows replay as timestamp-compared updates).
    if (this.localFirstWrites()) {
      // Base the merge on the freshest DURABLE copy when the caller couldn't
      // supply one: an id that was just remapped local_→server resolves to
      // nothing in Redux, and merging onto `{}` produced a skeleton row that
      // the replay then applied over the real cart (2026-08-13 item loss).
      let base = currentOrder;
      if (!base) {
        base = await localDB.getOrder(this._rowKeyFor({}, orderId) || orderId).catch(() => null);
        if (!base) {
          const entities = this._store?.getState()?.orders?.entities || {};
          base =
            entities[orderId] ||
            Object.values(entities).find(
              (o) =>
                String(o?._id || o?.id) === String(orderId) ||
                (o?.localId && String(o.localId) === String(orderId))
            ) ||
            null;
        }
        if (base) {
          console.warn('[offline] updateOrder recovered a base order for a stale id', {
            orderId, resolvedId: base._id || base.id || base.localId || null,
          });
        }
      }
      const saved = await this._persistOrderLocally({
        ...(base || {}),
        ...orderData,
        localId: base?.localId || currentOrder?.localId || orderId,
      });
      // Tombstone AFTER the durable persist — recording at entry left a false
      // "closed" marker when the write was later rejected (review finding).
      // Alias source is the recovered base when the caller supplied none, so
      // the server-id alias is tombstoned even for a stale local_ caller id.
      if (saved) this._recordCloseTombstone(orderId, base || currentOrder, orderData?.status);
      this._requestPump('updateOrder');
      return saved;
    }

    // If the id is still a local/draft idempotency key OR the Redux order is
    // flagged _isLocal, the row does not exist on the server yet — skip the
    // doomed PUT (which would just 404), persist locally instead, and let the
    // next sync replay the update with the same local_id so it merges into
    // the synced server row. Closes the silent-data-gap loophole where a
    // markBilledAt / item modification on a freshly-created order would log
    // a 404 and then drop the field on the floor.
    if (isLocalishOrderId(orderId) || currentOrder?._isLocal) {
      console.warn('[offline] updateOrder skipping server (id not synced yet) — persisting locally', {
        orderId,
        isLocalId: isLocalishOrderId(orderId),
        currentOrderIsLocal: !!currentOrder?._isLocal,
        fields: Object.keys(orderData || {}),
      });
      const saved = await this._persistOrderLocally({ ...(currentOrder || {}), ...orderData, localId: currentOrder?.localId || orderId });
      if (saved) this._recordCloseTombstone(orderId, currentOrder, orderData?.status);
      return saved;
    }

    try {
      const response = await orderService.updateOrder(orderId, orderData);
      // Server acknowledged the completion — safe to tombstone. A rejected
      // write must NOT record one (the order is still active server-side).
      this._recordCloseTombstone(orderId, currentOrder, orderData?.status);
      return response.order || response;
    } catch (error) {
      if (isNetworkError(error)) {
        console.warn('[offline] updateOrder server unreachable — queued locally', {
          orderId,
          errCode: error?.code,
          fields: Object.keys(orderData || {}),
        });
        this._handleNetworkLoss();
        const saved = await this._persistOrderLocally({ ...(currentOrder || {}), ...orderData, localId: currentOrder?.localId || orderId });
        if (saved) this._recordCloseTombstone(orderId, currentOrder, orderData?.status);
        return saved;
      }
      throw error;
    }
  }

  /**
   * Change an order's status.
   * Also logs a KDS event so we can replay status transitions during sync.
   */
  async updateOrderStatus(orderId, status, currentOrder, updates = {}) {
    const now = new Date().toISOString();

    if (this.shouldUseLocal()) {
      const row = await this._persistStatusLocally(orderId, status, now, currentOrder, updates);
      if (row) this._recordCloseTombstone(orderId, currentOrder, status);
      return row || { _id: orderId, id: orderId, status, updatedAt: now };
    }

    // ── Phase B local-first (Electron): persist + KDS event locally, return
    // the FULL merged row (callers upsert the return value — a bare stub
    // would be a field-wiping hazard), pump replays in the background.
    if (this.localFirstWrites()) {
      const row = await this._persistStatusLocally(orderId, status, now, currentOrder, updates);
      // Tombstone only when a durable row actually recorded the close.
      if (row) this._recordCloseTombstone(orderId, currentOrder, status);
      this._requestPump('updateOrderStatus');
      return row || { _id: orderId, id: orderId, status, updatedAt: now };
    }

    // Same local/draft-id guard as updateOrder — the row doesn't exist on
    // the server yet, so persist locally and let sync remap.
    if (isLocalishOrderId(orderId) || currentOrder?._isLocal) {
      console.warn('[offline] updateOrderStatus skipping server (id not synced yet) — persisting locally', {
        orderId,
        status,
        isLocalId: isLocalishOrderId(orderId),
      });
      const localRow = await this._persistStatusLocally(orderId, status, now, currentOrder, updates);
      if (localRow) this._recordCloseTombstone(orderId, currentOrder, status);
      return { _id: orderId, id: orderId, status, updatedAt: now };
    }

    try {
      const response = await orderService.updateOrderStatus(orderId, status, updates);
      // Server acknowledged — safe to tombstone; a rejected write records nothing.
      this._recordCloseTombstone(orderId, currentOrder, status);
      return response.order || response;
    } catch (error) {
      if (isNetworkError(error)) {
        this._handleNetworkLoss();
        const row = await this._persistStatusLocally(orderId, status, now, currentOrder, updates);
        if (row) this._recordCloseTombstone(orderId, currentOrder, status);
        return { _id: orderId, id: orderId, status, updatedAt: now };
      }
      throw error;
    }
  }

  /**
   * Mark an order as served (sets lastServedAt checkpoint).
   * Mimics the backend `/orders/:id/serve` behaviour locally.
   */
  async markOrderServed(orderId, status = 'served', currentOrder) {
    const now = new Date().toISOString();

    if (this.shouldUseLocal() || this.localFirstWrites()) {
      // Route through _persistStatusLocally: it owns the newer-wins merge,
      // the modification-history union, and the PAID preservation guard —
      // the old hand-rolled write here skipped the paid guard, so a serve
      // racing a payment could strip paymentMethod off the durable row.
      // It also refuses to mint a skeleton row for an unknown id.
      const row = await this._persistStatusLocally(orderId, status, now, currentOrder, {
        lastServedAt: now,
        last_served_at: now,
      });
      this._requestPump('markOrderServed');
      return row
        ? { ...row }
        : { _id: orderId, id: orderId, status, lastServedAt: now, updatedAt: now };
    }

    try {
      const response = await orderService.markOrderServed(orderId, status);
      return response;
    } catch (error) {
      if (isNetworkError(error)) {
        this._handleNetworkLoss();
        const row = await this._persistStatusLocally(orderId, status, now, currentOrder, {
          lastServedAt: now,
          last_served_at: now,
        });
        return row
          ? { ...row }
          : { _id: orderId, id: orderId, status, lastServedAt: now, updatedAt: now };
      }
      throw error;
    }
  }

  /**
   * Persist a Redux-optimistic order to localDB.
   * Call this after local-only modifications (add/remove item, finish modification).
   */
  async persistOrderLocally(order) {
    // Server-id keying, same rule as _persistOrderLocally (edit rows must not
    // keep a stale local_ key after sync — see _rowKeyFor).
    const localId = this._rowKeyFor(order);
    if (!localId) return;
    const now = new Date().toISOString();

    // Merge modification history with whatever is already in IndexedDB.
    // This prevents a partial Redux snapshot from silently overwriting complete
    // history that was written by an earlier finishOrderModification call.
    let mergedHistory = order.modificationHistory || order.modification_history || [];
    try {
      const existingInDB = await localDB.getOrder(localId);
      if (existingInDB) {
        const dbHistory = existingInDB.modificationHistory || existingInDB.modification_history || [];
        const dbTs = new Set(dbHistory.map(e => e.modifiedAt || e.modified_at).filter(Boolean));
        const newEntries = mergedHistory.filter(e => {
          const ts = e.modifiedAt || e.modified_at;
          return !ts || !dbTs.has(ts);
        });
        mergedHistory = [...dbHistory, ...newEntries];
      }
    } catch (_) { /* non-fatal — fall back to incoming history */ }

    await localDB.saveOrder({
      ...order,
      localId,
      _isLocal:           true,
      updatedAt:          now,
      branchId:           order.branchId || this._branchId,
      modificationHistory: mergedHistory,
      modification_history: mergedHistory,
    });
    await this._updatePendingCounts();
    this._requestPump('persistOrderLocally');
  }

  // ─── Sync — Online Mode (auto, triggered on network restore) ────────────────

  /**
   * Push all locally buffered orders and KDS events to the server in batches
   * of SYNC_BATCH_SIZE (50). Each batch is an independent HTTP request so no
   * single payload can time-out or exceed memory limits regardless of how long
   * the device was offline.
   *
   * Deep Fix A: batching + sync lock
   * Deep Fix F: retention age check before sending
   *
   * @param {string}   branchId
   * @param {Function} onProgress  ({ stage, total, processed, synced, failed, conflicts }) => void
   */
  async syncOnlineMode(branchId, onProgress, opts = {}) {
    // Deep Fix A: prevent concurrent syncs (e.g. auto-reconnect + manual button)
    if (this._syncInProgress) {
      throw new Error('Sync already in progress on this device');
    }
    this._syncInProgress = true;

    try {
      const bid = branchId || this._branchId;

      // ── Step 0: sync any locally-created sessions first ────────────────────
      // This must happen before orders so we can replace temp session IDs with
      // real server IDs on the orders before they are uploaded.
      const sessionIdMap = await this._syncSessions(bid);

      const [allOrdersRaw, kdsEvents] = await Promise.all([
        localDB.getAllOrders(bid),
        localDB.getAllKDSEvents(bid),
      ]);

      // Never upload drafts. A draft is a cart still being built — the real
      // order is created by POST /orders when Place Order is pressed. Stale
      // drafts that leak into localDB (e.g. saved as an offline fallback) used
      // to be inserted by the sync endpoint as ghost orders that later
      // resurfaced with phantom kitchen prints. The server also rejects them
      // now, but filtering here avoids ever counting them as pending.
      // Also upload only PENDING rows (never-synced ids or _isLocal edits) —
      // fully-acked rows are dead weight the server already has.
      const allOrders = allOrdersRaw.filter((o) => {
        const oid = String(o._id || o.id || '');
        const isDraft = oid.startsWith('draft_') || o.status === 'draft' || o.isDraft === true;
        if (isDraft) return false;
        // Offline-Mode manual sync pushes ONLY completed orders by design
        // (non-completed stay local until staff decide). It now rides THIS
        // engine — per-row outcomes, session sync, settle — instead of the
        // old syncOfflineMode, which deleted every completed row without any
        // per-row acknowledgment and never remapped offline sessions.
        if (opts.completedOnly && o.status !== 'completed') return false;
        const key = String(o.localId || o._id || o.id || '');
        return key.startsWith('local_') || o._isLocal === true;
      });

      // ── Remap temp session_id values on pending orders ──────────────────────
      if (Object.keys(sessionIdMap).length > 0) {
        for (const order of allOrders) {
          const localSid = order.session_id || order.sessionId;
          if (localSid && sessionIdMap[localSid]) {
            order.session_id = sessionIdMap[localSid];
            order.sessionId  = sessionIdMap[localSid];
            // Persist the updated session_id so the server receives the real ID
            await localDB.saveOrder(order);
          }
        }
      }

      // Deep Fix F: warn if local data is older than RETENTION_WARNING_DAYS
      await this._checkRetentionWarning(bid, allOrders);

      console.warn('[sync] online-sync starting', {
        branchId: bid,
        pendingOrders: allOrders.length,
        kdsEvents: kdsEvents.length,
        sessionsRemapped: Object.keys(sessionIdMap).length,
      });

      // Outstanding "delete the server twin" markers ride the last batch; the
      // server resolves them by local_id (works even when this device never
      // learned the server id) and echoes back what it settled. Read them
      // BEFORE the fast path — a marker-only sync (queue empty, one deleted
      // mistake order) must still reach the server.
      const { getDeletedLocalIds, clearResolvedDeletedLocalIds } = await import('./deletedLocalMarkers');
      const deletedLocalIds = getDeletedLocalIds();

      // Fast path — nothing to sync, still fetch reconciled state from server
      if (allOrders.length === 0 && kdsEvents.length === 0) {
        onProgress?.({ stage: 'uploading', total: 0, processed: 0, synced: 0, failed: 0, conflicts: 0 });
        const response = await api.post('/sync/online-sync', {
          branchId: bid, orders: [], kdsEvents: [], clientTime: new Date().toISOString(), isLastBatch: true,
          ...(deletedLocalIds.length > 0 ? { deletedLocalIds } : {}),
        }, { timeout: 20_000 });
        const result = response.data || {};
        if (Array.isArray(result.resolvedDeletedLocalIds)) {
          clearResolvedDeletedLocalIds(result.resolvedDeletedLocalIds);
        }
        await localDB.setMeta(`lastSync_${bid}`, new Date().toISOString());
        await this._updatePendingCounts();
        return {
          synced: 0, failed: 0, conflicts: 0, errors: [],
          reconciledOrders: result.reconciledOrders || [],
          reconciledTables: result.reconciledTables || [],
          localIdMap:       result.localIdMap       || {},
        };
      }

      const totals = { synced: 0, failed: 0, conflicts: 0, errors: [] };
      const combinedLocalIdMap = {};
      const combinedRowOutcomes = {};
      let resolvedDeletedLocalIds = [];
      let reconciledOrders = [];
      let reconciledTables = [];

      // Deep Fix A: send orders in chunks; KDS events travel with their batch's orders.
      // Loop runs at least once so pending KDS events still upload when zero
      // orders are pending (they used to strand: never sent, then cleared).
      const allOrderIds = new Set(allOrders.map(o => o.localId || o._id));
      const uploadedEventIds = new Set();
      for (let offset = 0; offset < Math.max(allOrders.length, 1); offset += SYNC_BATCH_SIZE) {
        const batch          = allOrders.slice(offset, offset + SYNC_BATCH_SIZE);
        const batchOrderIds  = new Set(batch.map(o => o.localId || o._id));
        const isLastBatch    = (offset + SYNC_BATCH_SIZE) >= allOrders.length;
        // Last batch also carries orphan events (orders synced in an earlier
        // round, or ids the row store no longer holds) so nothing strands.
        const batchKDSEvents = kdsEvents.filter(e =>
          batchOrderIds.has(e.orderId) || (isLastBatch && !allOrderIds.has(e.orderId))
        );
        batchKDSEvents.forEach(e => { if (e.eventId) uploadedEventIds.add(e.eventId); });

        onProgress?.({
          stage:     'uploading',
          total:     allOrders.length,
          processed: offset,
          synced:    totals.synced,
          failed:    totals.failed,
          conflicts: totals.conflicts,
        });

        // 60s (not the 30s axios default): a 50-order chunk with full item
        // payloads over a restaurant's degraded uplink can legitimately need
        // more than 30s — timing out a transfer that WOULD have finished just
        // converts slow sync into "Sync Failed" (Biryani House screenshot).
        const response = await api.post('/sync/online-sync', {
          branchId:   bid,
          orders:     batch,
          kdsEvents:  batchKDSEvents,
          clientTime: new Date().toISOString(),
          isLastBatch,
          ...(isLastBatch && deletedLocalIds.length > 0 ? { deletedLocalIds } : {}),
        }, { timeout: 60_000 });

        const result = response.data || {};
        totals.synced    += result.synced    ?? 0;
        totals.failed    += result.failed    ?? 0;
        totals.conflicts += result.conflicts ?? 0;
        totals.errors.push(...(result.errors ?? []));
        Object.assign(combinedLocalIdMap, result.localIdMap ?? {});
        Object.assign(combinedRowOutcomes, result.rowOutcomes ?? {});
        if (Array.isArray(result.resolvedDeletedLocalIds)) {
          resolvedDeletedLocalIds = result.resolvedDeletedLocalIds;
        }

        // Only the last batch returns the heavy reconciled-orders/tables query
        if (result.reconciledOrders) reconciledOrders = result.reconciledOrders;
        if (result.reconciledTables) reconciledTables = result.reconciledTables;
      }

      onProgress?.({
        stage:     'cleaning',
        total:     allOrders.length,
        processed: allOrders.length,
        synced:    totals.synced,
        failed:    totals.failed,
        conflicts: totals.conflicts,
      });

      // Settle ONLY what was uploaded, only if unchanged since upload, and
      // only if the SERVER's per-row outcome proves it is done with the row —
      // never a wholesale clear. Phase B's pump runs this engine DURING live
      // service: a write that lands between the upload snapshot and this
      // point must survive to the next sync, and a row the server rejected or
      // skipped must stay queued for retry, or the only durable copy of an
      // order dies (adversarial review: critical). Fix 1's local_id dedup
      // makes re-sending already-synced rows safe.
      await this._settleSyncedRows(bid, allOrders, uploadedEventIds, combinedRowOutcomes, totals);

      // Clear "delete the server twin" markers the server confirmed handling.
      clearResolvedDeletedLocalIds(resolvedDeletedLocalIds);

      // Storage cleanup after a successful sync: stale drafts, old KDS events,
      // SQLite VACUUM. Throttled — the Phase B pump runs this engine every few
      // seconds during service and a VACUUM per order write would thrash disk.
      const CLEANUP_MIN_INTERVAL_MS = 30 * 60 * 1000;
      if (!this._lastCleanupAt || Date.now() - this._lastCleanupAt > CLEANUP_MIN_INTERVAL_MS) {
        this._lastCleanupAt = Date.now();
        const cleanupRes = await localDB.cleanupStorage(bid);
        console.warn('[sync] post-sync storage cleanup', { branchId: bid, ...cleanupRes });
      }

      // Clear the oldest-order tracking now that data is synced
      await localDB.setMeta(`oldestUnsynced_${bid}`, null);

      console.warn('[sync] online-sync completed', {
        branchId: bid,
        synced: totals.synced,
        failed: totals.failed,
        conflicts: totals.conflicts,
        reconciledOrders: reconciledOrders.length,
        reconciledTables: reconciledTables.length,
        errors: totals.errors?.length || 0,
      });
      await localDB.setMeta(`lastSync_${bid}`, new Date().toISOString());
      await this._updatePendingCounts();

      // Clear retention warning from Redux now that sync succeeded
      this._dispatchSlice('offline/clearRetentionWarning', undefined);

      return {
        ...totals,
        reconciledOrders,
        reconciledTables,
        localIdMap: combinedLocalIdMap,
      };
    } catch (syncErr) {
      // Re-throw so the caller still sees the failure, but log it so a sync
      // crash mid-flight surfaces in the cloud feed even when no user-facing
      // alert fires.
      console.error('[sync] online-sync FAILED', {
        branchId: branchId || this._branchId,
        errCode: syncErr?.code,
        errStatus: syncErr?.response?.status,
        errMsg: syncErr?.message,
      });
      // Network-class failure (timeout / unreachable / no response) means the
      // "restored" network wasn't really usable. Flip back to offline so the
      // recovery probe restarts — when the API genuinely answers again,
      // restore fires and the auto-sync retriggers on its own. Without this,
      // a failed auto-sync was a dead end: modal error, no retry until staff
      // pressed the button or the network flapped again.
      const isNetworkErr =
        syncErr?.code === 'ECONNABORTED' ||
        syncErr?.code === 'ERR_NETWORK' ||
        !syncErr?.response;
      if (isNetworkErr) {
        this._handleNetworkLoss();
      }
      throw syncErr;
    } finally {
      // Deep Fix A: always release the lock, even on error
      this._syncInProgress = false;
    }
  }

  // ─── Sync — Offline Mode (manual) ───────────────────────────────────────────
  // The old syncOfflineMode is GONE. It POSTed completed orders to
  // /sync/offline-sync and then deleted every completed local row without
  // per-row acknowledgment (a single server-side rejection destroyed the only
  // durable copy of a paid order), and it never synced offline-created
  // sessions, so on session-mode branches every insert failed on the invalid
  // session id — and was deleted anyway. Offline-Mode manual sync now rides
  // the ONLINE engine with { completedOnly: true } (SyncProgressModal →
  // syncRunner.runOnlineSyncAndApply): per-row rowOutcomes, session sync and
  // remap, batching, markers, and the compare-and-delete settle all apply.


  // ─── Helpers ─────────────────────────────────────────────────────────────────

  /**
   * Return all orders stored locally in IndexedDB for the active branch.
   * Used as a fallback when the network is unreachable.
   */
  async getLocalOrders(branchId) {
    const bid = branchId || this._branchId;
    return localDB.getAllOrders(bid);
  }

  async getLastSync(branchId) {
    return localDB.getMeta(`lastSync_${branchId || this._branchId}`);
  }

  async getPendingCounts(branchId) {
    const bid = branchId || this._branchId;
    const [orders, events] = await Promise.all([
      localDB.getAllOrders(bid),
      localDB.getAllKDSEvents(bid),
    ]);
    // Only orders that HAVE NEVER SYNCED count as pending. `_persistOrderLocally`
    // gets called on many defensive paths — post-write, post-item-modify, etc.
    // — so localDB accumulates shadows of server-authoritative orders that
    // aren't actually waiting to sync. Counting the shadow copies as
    // "pending" left the yellow banner stuck at "N items pending" long
    // after everything was on the server. A truly-pending order is one
    // whose id (or localId) is still a local_ / draft_ prefix.
    const isPending = (o) => {
      const id = String(o?.localId || o?._id || o?.id || '');
      // Drafts are carts, not orders — they never sync, so counting them kept
      // the pump looping on no-op rounds and the banner stuck on stale carts.
      if (id.startsWith('draft_') || o?.status === 'draft' || o?.isDraft === true) return false;
      return id.startsWith('local_') || o?._isLocal === true;
    };
    const pendingOrders = (orders || []).filter(isPending);
    // Deletes of an ALREADY-SYNCED order queue separately (orderThunks.js
    // PENDING_DELETES_KEY, not localDB) and were invisible here — so a
    // session whose only offline action was a delete never tripped the
    // "pending > 0" gate below, and the reconnect pump's own no-op check
    // (_runQuietSync) skipped it too. The order stayed deleted locally but
    // never told the server, and resurrected on the next fetch (Istanbul,
    // Antalya, Biryani House ghost orders — all this exact gap). Counting it
    // here makes every existing "pending > 0" check see it for free.
    let pendingDeletes = 0;
    try {
      const raw = JSON.parse(localStorage.getItem('pos_pending_order_deletes') || '[]');
      pendingDeletes = Array.isArray(raw) ? raw.length : 0;
    } catch (_) { /* unreadable — treat as none, flush will retry regardless */ }
    return { orders: pendingOrders.length + pendingDeletes, kdsEvents: events.length };
  }

  /**
   * Public wrapper for _updatePendingCounts — lets the UI force a Redux
   * refresh when it suspects the counts have drifted from what's actually
   * in localDB (banner stuck on "N items pending" after a sync that
   * silently cleared them, etc.). Safe to call on any tick.
   */
  async refreshPendingCounts() {
    await this._updatePendingCounts();
  }

  // ─── Private ─────────────────────────────────────────────────────────────────

  async _saveNewOrderLocally(orderPayload, branchId) {
    // Reuse the idempotency key from createOrder when present so a lost-response
    // fallback keeps the same local_id the server already stored; only mint a new
    // one for orders that start their life offline.
    const _payloadHadLocalId = !!(orderPayload.localId || orderPayload.local_id);
    const localId = orderPayload.localId || orderPayload.local_id || generateLocalId('offlineManager._saveNewOrderLocally');
    if (!_payloadHadLocalId) {
      console.warn('[create-order] _saveNewOrderLocally minted fresh localId', { localId, branchId });
    }
    const now     = new Date().toISOString();
    const bid     = branchId || this._branchId;

    // Generate a temporary offline order number so staff can reference orders by name
    // while internet is down.  Format: L-0001, L-0002, …
    // The server replaces this with a real sequential number after sync via localIdMap.
    // The counter is a PERSISTENT meta value, not a scan of queued rows: under
    // Phase B the pump settles (deletes) rows seconds after they sync, so a
    // scan-derived max reset to zero constantly and every takeaway printed the
    // same "L-0001" kitchen label (adversarial review, prints-modification).
    let tempOrderNumber = orderPayload.orderNumber || null;
    if (!tempOrderNumber) {
      // Serialize the read-increment-write: two rapid creates interleaving at
      // the await points must not read the same counter value.
      this._seqLock = (this._seqLock || Promise.resolve()).then(async () => {
        try {
          const seqKey = `localOrderSeq_${bid}`;
          const prevSeq = parseInt(await localDB.getMeta(seqKey), 10) || 0;
          // Also respect any queued rows from older builds so numbering never collides.
          const existingOrders = await localDB.getAllOrders(bid);
          const maxLocal = existingOrders.reduce((max, o) => {
            const n = String(o.orderNumber || o.order_number || '');
            if (/^L-?\d+$/.test(n)) {
              const num = parseInt(n.replace(/^L-?/, ''), 10);
              return isNaN(num) ? max : Math.max(max, num);
            }
            return max;
          }, 0);
          const next = Math.max(prevSeq, maxLocal) + 1;
          await localDB.setMeta(seqKey, String(next));
          return `L-${String(next).padStart(4, '0')}`;
        } catch (_) {
          return null; /* non-fatal */
        }
      });
      tempOrderNumber = await this._seqLock;
    }

    // Stamp session_id from the locally-cached active session.
    // This covers BOTH scenarios:
    //   • Pure offline   → local session has a temp localId ("local_session_...")
    //                       which gets remapped to a real UUID during sync
    //   • Online→offline → real session UUID was cached when session started online;
    //                       orders arrive at the server with the correct real ID
    let sessionId = orderPayload.session_id || orderPayload.sessionId || null;
    if (!sessionId) {
      try {
        const cachedSession = await localDB.getActiveSession(bid);
        if (cachedSession && cachedSession.status === 'active') {
          sessionId = cachedSession._id || cachedSession.id || cachedSession.localId || null;
        }
      } catch (_) { /* non-fatal */ }
    }

    // Stamp who placed the order — the sync endpoint uses this to preserve
    // per-user attribution. Without it, every offline-created order syncs with
    // server_id=null and breaks waiter sales reports + any dashboard that
    // filters by attributed sales.
    let serverId = orderPayload.serverId ?? orderPayload.server_id ?? null;
    if (!serverId) {
      try {
        const rawUser = localStorage.getItem('user');
        if (rawUser) {
          const parsed = JSON.parse(rawUser);
          serverId = parsed?.id || parsed?._id || null;
        }
      } catch (_) { /* non-fatal — orders are still saved without attribution */ }
    }

    const localOrder = {
      ...orderPayload,
      localId,
      _id:        localId,
      id:         localId,
      _isLocal:   true,
      branchId:   bid,
      ...(serverId ? { serverId, server_id: serverId } : {}),
      orderNumber: tempOrderNumber,
      createdAt:  orderPayload.createdAt || now,
      updatedAt:  now,
      ...(sessionId ? { session_id: sessionId, sessionId } : {}),
    };
    await localDB.saveOrder(localOrder);

    // Deep Fix F: track the earliest unsynced order creation time so we can
    // warn staff if data is approaching the retention threshold.
    try {
      const metaKey   = `oldestUnsynced_${bid}`;
      const existing  = await localDB.getMeta(metaKey);
      const orderTime = localOrder.createdAt;
      if (!existing || new Date(orderTime) < new Date(existing)) {
        await localDB.setMeta(metaKey, orderTime);
      }
    } catch (_) { /* non-fatal */ }

    await this._updatePendingCounts();
    return localOrder;
  }

  /**
   * Deep Fix F: check if any local data is older than RETENTION_WARNING_DAYS.
   * Dispatches a Redux warning so SyncProgressModal can surface it to staff.
   */
  async _checkRetentionWarning(branchId, allOrders) {
    try {
      const metaKey = `oldestUnsynced_${branchId}`;
      let oldest = await localDB.getMeta(metaKey);

      // Fallback: derive from orders array if meta key is missing
      if (!oldest && allOrders.length > 0) {
        const times = allOrders.map(o => new Date(o.createdAt || o.created_at || 0).getTime()).filter(Boolean);
        if (times.length > 0) oldest = new Date(Math.min(...times)).toISOString();
      }

      if (!oldest) return;

      const ageDays = (Date.now() - new Date(oldest).getTime()) / (1000 * 60 * 60 * 24);
      if (ageDays >= RETENTION_WARNING_DAYS) {
        this._dispatchSlice('offline/setRetentionWarning', {
          ageDays:     Math.floor(ageDays),
          oldestDate:  oldest,
        });
      }
    } catch (_) { /* non-fatal */ }
  }

  /**
   * Post-sync settle: remove an UPLOADED order row only when BOTH hold:
   *  1. the server's per-row outcome proves it is done with the row
   *     (applied / dedup / superseded / deleted — NOT failed, NOT missing), and
   *  2. the row is unchanged since the upload snapshot (updatedAt match) —
   *     a write landing mid-sync stays pending for the next round.
   * Rows the server failed stay queued and are loudly logged. When the server
   * predates rowOutcomes (deploy skew), settle only on a fully clean batch.
   * KDS events are cleared only when every event currently in the store was
   * part of this upload; otherwise they're left to re-send (server replay is
   * idempotent + status-monotone).
   */
  async _settleSyncedRows(branchId, uploadedOrders, uploadedEventIds, rowOutcomes, totals) {
    const SETTLED_OUTCOMES = new Set(['applied', 'dedup', 'superseded', 'deleted']);
    const hasOutcomes = rowOutcomes && Object.keys(rowOutcomes).length > 0;
    // Legacy-server fallback: no per-row outcomes → only settle when nothing
    // failed or conflicted at all (mirrors the old clear-only-on-success rule).
    const legacyCleanBatch =
      !hasOutcomes && (totals?.failed || 0) === 0 && (totals?.conflicts || 0) === 0;
    let settled = 0, kept = 0;
    const failedRows = [];
    const settledKeys = [];
    for (const uploaded of uploadedOrders || []) {
      const key = uploaded.localId || uploaded._id || uploaded.id;
      if (!key) continue;
      const outcome = hasOutcomes ? rowOutcomes[String(key)] : (legacyCleanBatch ? 'applied' : undefined);
      if (!SETTLED_OUTCOMES.has(outcome)) {
        kept++;
        failedRows.push({ key: String(key), outcome: outcome || 'unknown', orderNumber: uploaded.orderNumber || null, status: uploaded.status || null });
        continue;
      }
      try {
        // Atomic compare-and-delete (SQLite) — a write landing between the
        // upload and this settle keeps its row for the next round.
        const res = await localDB.deleteOrderIfUnchanged(key, uploaded.updatedAt);
        if (res?.deleted === 1) {
          settled++;
          settledKeys.push(String(key));
        } else if (res?.missing) {
          settledKeys.push(String(key)); // already gone — flag still clears
        } else {
          const current = await localDB.getOrder(key).catch(() => null);
          if (!current) settledKeys.push(String(key)); // gone between calls
          else kept++; // newer local write during sync — pending for next round
        }
      } catch (_) { /* leave the row; re-sync is idempotent */ }
    }
    // Drop the Redux _isLocal marker for settled rows — a flag that never
    // clears made the modal reconcile's local-preservation shadow fresher
    // server truth forever (fix-verification, quiet-apply major).
    if (settledKeys.length > 0) {
      this._dispatchSlice('orders/clearLocalFlags', settledKeys);
    }
    if (failedRows.length > 0) {
      // These rows carry order/payment state the server does NOT have yet —
      // they stay queued for retry, and this line is the operator's signal.
      console.error('[sync] rows NOT settled — server did not accept them', {
        branchId, count: failedRows.length, rows: failedRows.slice(0, 10),
      });
    }
    try {
      const eventsNow = await localDB.getAllKDSEvents(branchId);
      const allUploaded = (eventsNow || []).every(
        (e) => !e.eventId || uploadedEventIds?.has?.(e.eventId)
      );
      // Events for a FAILED order were skipped server-side (its local id never
      // mapped) — keep the whole store so they re-send with the retried order.
      if (allUploaded && failedRows.length === 0) {
        await localDB.clearKDSEvents(branchId);
      } else {
        kept += 1;
      }
    } catch (_) { /* non-fatal */ }
    console.warn('[sync] settled synced rows', { branchId, settled, keptPending: kept });
  }

  async _persistOrderLocally(order) {
    // SKELETON GUARD — never queue a row that carries neither a cart nor a
    // status. Such a row says nothing, but the sync replay applied it as an
    // update and BLANKED the server's cart: five orders across four branches
    // lost every item on 2026-08-13 (Antalya T0449 and friends, ~₺4,430 of
    // open bills) while their totals survived, hiding them from the takeaway
    // board. Root trigger: a write dispatched against an id that had just
    // been remapped local_→server, so the caller's order lookup came back
    // empty and only `{localId}` survived the merge. The server now refuses
    // such payloads too; this stops them ever being written.
    const hasItems  = Array.isArray(order?.items) && order.items.length > 0;
    const hasStatus = !!order?.status;
    if (!hasItems && !hasStatus) {
      console.error('[offline] REFUSED to queue a skeleton order row (no items, no status)', {
        orderId: order?._id || order?.id || order?.localId || null,
        keys: Object.keys(order || {}).slice(0, 12),
      });
      return null;
    }

    // Key by the SERVER id once one exists (see _rowKeyFor): an edit row that
    // kept its original local_ key after sync would hit the replay's
    // create-path dedup and be DISCARDED instead of applied as an update.
    const localId = this._rowKeyFor(order) || order.localId || generateLocalId('offlineManager._persistOrderLocally');
    const now     = new Date().toISOString();
    const saved   = {
      ...order,
      localId,
      _isLocal:  true,
      updatedAt: now,
      branchId:  order.branchId || this._branchId,
    };

    // PAYMENT-FIELD PRESERVATION. The queue keeps ONE row per order and this
    // save replaces it — a status-only write built from a Redux snapshot
    // without the payment fields silently stripped a queued cash payment
    // (Ayder order 1578, ₺2,640, 2026-08-10: staff tapped Cash offline, the
    // follow-up completion write clobbered the row, sync replayed a
    // completion with paid=false). Once a queued row says PAID, no later
    // write may unsay it.
    try {
      const prior = await localDB.getOrder(localId).catch(() => null);
      if (prior && prior.paid === true && saved.paid !== true) {
        saved.paid          = true;
        saved.paymentMethod = saved.paymentMethod || prior.paymentMethod || prior.payment_method;
        saved.paymentDate   = saved.paymentDate   || prior.paymentDate   || prior.payment_date;
        if (saved.cashAmount == null && prior.cashAmount != null) saved.cashAmount = prior.cashAmount;
        if (saved.cardAmount == null && prior.cardAmount != null) saved.cardAmount = prior.cardAmount;
        console.warn('[offline] queue write tried to strip PAID — preserved payment fields', {
          localId,
          incomingStatus: saved.status,
          paymentMethod: saved.paymentMethod || null,
        });
      }
    } catch (_) { /* preservation is best-effort; the save below still runs */ }

    // Every queue write is loggable — the Ayder forensics needed this line.
    console.warn('[offline] order persisted to local queue', {
      localId,
      status: saved.status || null,
      paid: saved.paid === true,
      paymentMethod: saved.paymentMethod || null,
      itemCount: Array.isArray(saved.items) ? saved.items.length : null,
    });

    try {
      await localDB.saveOrder(saved);
    } catch (err) {
      // Surface IndexedDB write failures loudly — silent failure here means a
      // payment was collected but the order's state isn't persisted anywhere,
      // which is one of the worst-case loopholes for offline mode.
      console.error('[offline] _persistOrderLocally FAILED — order may be lost', {
        localId,
        branchId: saved.branchId,
        errName: err?.name,
        errMsg: err?.message,
      });
      throw err;
    }
    await this._updatePendingCounts();
    return saved;
  }

  async _persistStatusLocally(orderId, status, now, currentOrder, updates = {}) {
    // Merge the localDB copy with the Redux snapshot, NEWER-WINS by updatedAt.
    // The old rule was DB-always-wins, which let a stale queue row overwrite a
    // fresher cross-device update that had arrived in Redux via socket — and
    // then replay that stale state to the server with a fresh timestamp
    // (adversarial review, edge-lifecycle critical).
    const fromDB = await localDB.getOrder(orderId).catch(() => null);
    let existing = currentOrder;
    if (fromDB && currentOrder) {
      const dbTime = new Date(fromDB.updatedAt || fromDB.updated_at || 0).getTime();
      const rxTime = new Date(currentOrder.updatedAt || currentOrder.updated_at || 0).getTime();
      existing = dbTime >= rxTime
        ? { ...currentOrder, ...fromDB }
        : { ...fromDB, ...currentOrder };
      // Modification history is a UNION, never a pick — the newer copy's
      // shallow win must not drop an entry only the other copy carries.
      const histA = fromDB.modificationHistory || fromDB.modification_history || [];
      const histB = currentOrder.modificationHistory || currentOrder.modification_history || [];
      if (histA.length || histB.length) {
        const seen = new Set(histA.map((e) => e.modifiedAt || e.modified_at).filter(Boolean));
        const merged = [
          ...histA,
          ...histB.filter((e) => {
            const ts = e.modifiedAt || e.modified_at;
            return !ts || !seen.has(ts);
          }),
        ];
        existing.modificationHistory = merged;
        existing.modification_history = merged;
      }
    } else if (fromDB) {
      existing = fromDB;
    }
    let savedRow = null;
    if (existing) {
      const row = {
        ...existing,
        // Extra fields riding the status change (e.g. paid/paymentMethod on a
        // completion) must land in the SAME row write — the online path sent
        // them to the server in one PUT; the local path used to drop them.
        ...(updates && typeof updates === 'object' ? updates : {}),
        localId:   this._rowKeyFor(existing, orderId),
        status,
        updatedAt: now,
        _isLocal:  true,
      };
      // Same PAID preservation as _persistOrderLocally: a status-only write
      // must never strip payment fields that either source already carries.
      const paidSource = [fromDB, currentOrder].find((s) => s && s.paid === true);
      if (paidSource && row.paid !== true) {
        row.paid          = true;
        row.paymentMethod = row.paymentMethod || paidSource.paymentMethod || paidSource.payment_method;
        row.paymentDate   = row.paymentDate   || paidSource.paymentDate   || paidSource.payment_date;
        // Split payments: the cash/card breakdown is payment data too — a
        // status write that stripped it broke the Z-report split totals.
        if (row.cashAmount == null && row.cash_amount == null) {
          const v = paidSource.cashAmount ?? paidSource.cash_amount;
          if (v != null) row.cashAmount = v;
        }
        if (row.cardAmount == null && row.card_amount == null) {
          const v = paidSource.cardAmount ?? paidSource.card_amount;
          if (v != null) row.cardAmount = v;
        }
        console.warn('[offline] status write tried to strip PAID — preserved payment fields', {
          orderId, status,
        });
      }
      console.warn('[offline] status persisted to local queue', {
        orderId, status, paid: row.paid === true,
      });
      await localDB.saveOrder(row);
      savedRow = row;
    }
    await this._logKDSEvent(orderId, status, now);
    await this._updatePendingCounts();
    return savedRow;
  }

  async _logKDSEvent(orderId, status, timestamp) {
    await localDB.saveKDSEvent({
      eventId:   generateLocalId('kdsEvent'),
      orderId,
      status,
      timestamp: timestamp || new Date().toISOString(),
      branchId:  this._branchId,
    });
  }

  /**
   * Sync locally-created sessions to the server before order sync.
   * Returns { localSessionId → realSessionId } map for order remapping.
   */
  async _syncSessions(branchId) {
    try {
      const pending = await localDB.getPendingSessions(branchId);
      if (!pending.length) return {};
      // Lazy import to avoid circular dependency
      const { default: sessionService } = await import('./sessionService');
      return sessionService.syncPendingSessions(branchId);
    } catch (err) {
      console.error('[offlineManager] Session sync failed (non-fatal):', err);
      return {};
    }
  }

  /**
   * Periodically ping the API so a free-tier/idle server doesn't spin down.
   * A spun-down server cold-starts the next order (15–50s) → it times out into
   * local storage and shows "waiting for sync" even though the internet is fine.
   * Keeping it warm avoids that during quiet periods.
   */
  _startKeepAlive() {
    if (this._keepAliveTimer) return;
    const KEEPALIVE_MS = 10 * 60 * 1000; // 10 min (servers idle out ~15 min)
    const ping = () => {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      api.get('/health', { timeout: 25000 }).catch(() => {});
    };
    ping(); // warm immediately on load
    this._keepAliveTimer = setInterval(ping, KEEPALIVE_MS);
  }

  _handleNetworkLoss() {
    // Circuit-breaker timestamp: every failure extends the offline cooldown so
    // a flapping network can't bounce the app back to online-first between
    // failures (see _handleNetworkRestore).
    this._lastNetFailureAt = Date.now();
    if (this._isNetworkOnline) {
      this._isNetworkOnline = false;
      this._dispatchSlice('offline/setNetworkOnline', false);
      // Always warn so it reaches the cloud log feed — operators need to see
      // when a counter drops offline even if no other errors fire.
      console.warn('[offline] network lost — switching to local writes', {
        branchId: this._branchId,
        mode: this._mode,
      });
    }
    // The OS 'online' event only fires when the network INTERFACE changes.
    // When the ISP drops but Wi-Fi stays connected (common in restaurants),
    // that event never fires — so probe the API until it answers again.
    this._startRecoveryProbe();
  }

  _startRecoveryProbe() {
    if (this._recoveryTimer) return;
    const PROBE_INTERVAL_MS = 20_000;
    this._recoveryTimer = setInterval(async () => {
      if (this._isNetworkOnline) {
        this._stopRecoveryProbe();
        return;
      }
      try {
        await api.get('/health', { timeout: 8000 });
        this._stopRecoveryProbe();
        this._handleNetworkRestore('probe');
      } catch (_) {
        /* still offline — keep probing */
      }
    }, PROBE_INTERVAL_MS);
  }

  _stopRecoveryProbe() {
    if (this._recoveryTimer) {
      clearInterval(this._recoveryTimer);
      this._recoveryTimer = null;
    }
  }

  _handleNetworkRestore(source = 'passive') {
    // Circuit breaker: on a flapping network, tiny requests (health keep-alive,
    // log flushes) intermittently succeed while heavy order POSTs time out.
    // Before this gate, ANY successful response flipped the app back to
    // online-first, so every Place Order paid the full write-timeout before
    // falling back to local again — staff experienced this as constant
    // freezes at Ayder. Now only the dedicated recovery probe (which proves
    // the API answers a fresh request) may restore within the cooldown window
    // after a failure; passive successes and OS events must wait it out.
    const OFFLINE_COOLDOWN_MS = 60_000;
    if (
      source !== 'probe' &&
      !this._isNetworkOnline &&
      this._lastNetFailureAt &&
      Date.now() - this._lastNetFailureAt < OFFLINE_COOLDOWN_MS
    ) {
      return; // still in cooldown — let the recovery probe decide
    }

    this._stopRecoveryProbe();
    if (!this._isNetworkOnline) {
      this._isNetworkOnline = true;
      this._dispatchSlice('offline/setNetworkOnline', true);
      console.warn('[offline] network restored — sync queued', {
        branchId: this._branchId,
        mode: this._mode,
      });

      // Electron (Phase B local-first) never shows the sync modal — the pump
      // is silent by design. But the pump only runs on the NEXT order write,
      // and a pending DELETE (unlike an order) doesn't generate one on its
      // own — so a branch whose only offline action was a delete could sit
      // resurrecting until an unrelated write or app restart happened to
      // flush it. Kick the pump directly on reconnect so a lone pending
      // delete clears the instant connectivity returns, not just eventually.
      if (this.localFirstWrites()) {
        this._requestPump('network-restore');
      }

      // In Online mode: trigger the sync modal automatically.
      // Deep Fix D: add random jitter (0–30s) so 10 devices reconnecting at the
      // same instant don't all hammer the server simultaneously (thundering herd).
      if (this._mode === 'online') {
        const jitterMs = Math.floor(Math.random() * 30_000);
        setTimeout(() => {
          // Re-check: another code path may have already started a sync during jitter
          if (this._syncInProgress) return;
          this._updatePendingCounts().then(async () => {
            const state   = this._store?.getState();
            const pending = state?.offline?.pendingOrdersCount    || 0;
            const events  = state?.offline?.pendingKDSEventsCount || 0;
            if (pending > 0 || events > 0) {
              // Pre-flight: prove the API answers RIGHT NOW before opening the
              // sync modal. On flapping networks the "restore" that got us here
              // may be a single lucky packet — starting the heavy chunk upload
              // then guarantees a 30s+ timeout and a "Sync Failed" alarm modal
              // in staff's faces (Biryani House report). If the probe fails,
              // flip back to offline; the recovery probe will re-trigger this
              // whole path when the API genuinely answers again.
              try {
                await api.get('/health', { timeout: 8000 });
                this._dispatchSlice('offline/setShowSyncModal', true);
              } catch (_) {
                console.warn('[offline] pre-sync health probe failed — staying offline', {
                  branchId: this._branchId,
                });
                this._handleNetworkLoss();
              }
            }
          });
        }, jitterMs);
      }

      // In Offline mode: notify staff that the network is back so they can
      // manually trigger a sync when ready.
      if (this._mode === 'offline') {
        this._updatePendingCounts().then(() => {
          const state   = this._store?.getState();
          const pending = state?.offline?.pendingOrdersCount    || 0;
          const events  = state?.offline?.pendingKDSEventsCount || 0;
          if (pending > 0 || events > 0) {
            // Re-use the sync modal — the UI already distinguishes online vs offline
            // mode via the Redux `offline/mode` slice, so the modal copy is correct.
            this._dispatchSlice('offline/setShowSyncModal', true);
          }
        });
      }
    }
  }

  async _updatePendingCounts() {
    if (!this._store || !this._branchId) return;
    try {
      const counts = await this.getPendingCounts(this._branchId);
      this._dispatchSlice('offline/setPendingCounts', {
        orders:    counts.orders,
        kdsEvents: counts.kdsEvents,
      });
    } catch (_) {
      // Ignore — counts are informational
    }
  }

  _dispatchSlice(type, payload) {
    this._store?.dispatch({ type, payload });
  }
}

const offlineManager = new OfflineManager();
export default offlineManager;
